/**
 * Enemy Collision System for Kobayashi Maru
 * Handles enemy collision with the Kobayashi Maru (main ship)
 * When enemies reach the ship, they explode and deal damage
 */
import { query, World, hasComponent } from 'bitecs';
import { Position, Health, Faction, AIBehavior } from '../ecs/components';
import { FactionId, GAME_CONFIG } from '../types/constants';
import { ParticleSystem, EFFECTS } from '../rendering';
import { AudioManager, SoundType } from '../audio';
import { AUDIO_CONFIG } from '../config';
import { applyDamage } from '../services';

/**
 * Creates the enemy collision system that handles enemy-ship collisions
 * @param particleSystem - Particle system for explosion effects
 * @param getKobayashiMaruId - Function to get the Kobayashi Maru entity ID
 * @returns System update function and methods
 */
export function createEnemyCollisionSystem(
  particleSystem?: ParticleSystem,
  getKobayashiMaruId?: () => number
) {
  // Track entities that should be destroyed this frame
  const entitiesToDestroy: number[] = [];

  function enemyCollisionSystem(world: World): World {
    // Clear destruction list from last frame
    entitiesToDestroy.length = 0;

    // Get Kobayashi Maru ID
    const kmId = getKobayashiMaruId?.() ?? -1;
    if (kmId === -1) {
      return world;
    }

    // Check if Kobayashi Maru exists and has health
    if (!hasComponent(world, kmId, Position) || !hasComponent(world, kmId, Health)) {
      return world;
    }

    const kmX = Position.x[kmId];
    const kmY = Position.y[kmId];

    // Get all enemies
    const enemies = query(world, [Position, Health, Faction, AIBehavior]);

    for (const eid of enemies) {
      // Skip Federation entities (should not be in enemyQuery, but double-check)
      if (Faction.id[eid] === FactionId.FEDERATION) {
        continue;
      }

      // Skip dead enemies (will be cleaned up by damage system)
      if (Health.current[eid] <= 0) {
        continue;
      }

      const enemyX = Position.x[eid];
      const enemyY = Position.y[eid];

      // Check collision with Kobayashi Maru
      const dx = enemyX - kmX;
      const dy = enemyY - kmY;
      const distanceSquared = dx * dx + dy * dy;
      const collisionRadius = GAME_CONFIG.ENEMY_COLLISION_RADIUS;
      const collisionRadiusSquared = collisionRadius * collisionRadius;

      if (distanceSquared <= collisionRadiusSquared) {
        // Collision detected!

        // Mark enemy for destruction (set health to 0) - DO THIS FIRST
        Health.current[eid] = 0;
        entitiesToDestroy.push(eid);

        // Deal damage to Kobayashi Maru (shields first, then hull)
        applyDamage(world, kmId, GAME_CONFIG.ENEMY_COLLISION_DAMAGE);

        // Spawn explosion effect at enemy position
        if (particleSystem) {
          try {
            particleSystem.spawn({
              ...EFFECTS.EXPLOSION_LARGE,
              x: enemyX,
              y: enemyY
            });
          } catch (e) {
            console.error('Failed to spawn explosion particle:', e);
          }
        }

        // Play explosion sound
        try {
          AudioManager.getInstance().play(SoundType.EXPLOSION_SMALL, { volume: AUDIO_CONFIG.SFX.EXPLOSION_SMALL });
        } catch (e) {
          console.error('Failed to play explosion sound:', e);
        }
      }
    }

    return world;
  }


  return {
    update: enemyCollisionSystem,
    /**
     * Get entities that were destroyed by collision this frame
     */
    getDestroyedThisFrame: () => [...entitiesToDestroy]
  };
}

export type EnemyCollisionSystem = ReturnType<typeof createEnemyCollisionSystem>;
